import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FileDown, FileSpreadsheet, ImagePlus, LogOut, MessageCircle, Phone, Plus, Ruler, Trash2, X } from 'lucide-react';
import { API } from '../constants/site';
import { resolveImg, SITE_IMAGE_GROUPS } from '../constants/siteImages';
import { downloadDiagramPNG, downloadDiagramPDF, saveBlob } from '../utils/diagramExport';
import { GarageDiagram } from '../components/GarageDiagram';
import { useSEO } from '../components/SEO';
import { Overline } from '../components/motion';

const inputCls = 'w-full bg-transparent border-b border-line/20 py-2.5 text-ink text-sm focus:border-accent focus:outline-none transition-colors';
const btnCls = 'text-xs font-bold uppercase tracking-[0.15em] px-4 py-2.5 transition-colors duration-300';

const CATEGORIES = ['pvc', 'garaj', 'automatizari', 'rulouri'];

const DIM_SHORT = {
  latime_gol: 'L', inaltime_gol: 'H', spatiu_stanga: 'S', spatiu_dreapta: 'D',
  lintel: 'HG', adancime: 'DG', inaltime_totala: 'HT', textura: 'Textură', culoare: 'Culoare', model: 'Model',
};

const LEAD_STATUS_OPTIONS = [
  ['new', 'Nou'],
  ['contactat', 'Contactat'],
  ['ofertat', 'Ofertat'],
  ['fara_raspuns', 'Nu a răspuns'],
];

const STATUS_STYLE = {
  new: 'text-ink',
  contactat: 'text-[#0E9F6E]',
  ofertat: 'text-accent',
  fara_raspuns: 'text-smoke',
};

const STATUS_DOT = {
  new: 'bg-accent',
  contactat: 'bg-[#0E9F6E]',
  ofertat: 'bg-[#8B5CF6]',
  fara_raspuns: 'bg-smoke/60',
};

const SVC_LABEL = { pvc: 'Tâmplărie PVC', garaj: 'Porți de garaj', automatizari: 'Automatizări', rulouri: 'Rulouri' };

const waLink = (phone) => {
  let d = String(phone || '').replace(/\D/g, '');
  if (d.startsWith('0040')) d = d.slice(2);
  else if (d.startsWith('0')) d = `4${d}`;
  return `https://wa.me/${d}`;
};

const stripDia = (s) => String(s).normalize('NFD').replace(/[̀-ͯ]/g, '');

const buildPdfRows = (m) => {
  const rows = [];
  ['latime_gol', 'inaltime_gol', 'spatiu_stanga', 'spatiu_dreapta', 'lintel', 'adancime'].forEach((k) => {
    if (m[k]) rows.push(`${DIM_SHORT[k]}: ${m[k]} mm`);
  });
  if (m.model) rows.push(`Model: ${stripDia(m.model)}`);
  if (m.culoare) rows.push(`Culoare: ${stripDia(m.culoare)}`);
  return rows;
};

const ImageSlotCard = ({ slot, custom, uploading, onReplace }) => (
  <div data-testid={`admin-image-slot-${slot.key}`} className="border border-line/10 bg-surface rounded-[2rem] overflow-hidden hover:shadow-[0_20px_60px_rgba(24,26,32,0.08)] transition-shadow duration-500">
    <div className="relative">
      <img src={custom ? resolveImg(custom) : slot.def} alt={slot.label} className="w-full h-40 object-cover" />
      <span className={`absolute top-3 left-3 text-[9px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-full ${custom ? 'bg-accent text-white' : 'bg-black/55 text-white'}`}>
        {custom ? 'personalizată' : 'implicită'}
      </span>
    </div>
    <div className="p-4 flex items-center justify-between gap-3">
      <p className="text-sm font-medium leading-snug">{slot.label}</p>
      <label className="text-xs font-bold uppercase tracking-[0.15em] px-4 py-2.5 rounded-full bg-ink text-paper hover:bg-accent hover:text-white transition-colors duration-300 cursor-pointer shrink-0">
        {uploading ? 'Se încarcă…' : 'Înlocuiește'}
        <input
          data-testid={`admin-image-upload-${slot.key}`}
          type="file"
          accept="image/*"
          className="hidden"
          disabled={uploading}
          onChange={(e) => { onReplace(slot.key, e.target.files?.[0]); e.target.value = ''; }}
        />
      </label>
    </div>
  </div>
);

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem('dd-admin-token') || '');
  useSEO('admin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [tab, setTab] = useState('leaduri');
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [leadFilter, setLeadFilter] = useState(null);
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [schemaLead, setSchemaLead] = useState(null);
  const [pdfLead, setPdfLead] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  const [brands, setBrands] = useState([]);
  const [siteImages, setSiteImages] = useState({});
  const [uploading, setUploading] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [newItem, setNewItem] = useState({ category: 'pvc', title: '', location: '' });
  const [newImages, setNewImages] = useState([]);
  const [newBrand, setNewBrand] = useState('');

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadAll = useCallback(async () => {
    try {
      const [l, p, b, si, st] = await Promise.all([
        axios.get(`${API}/leads`, auth),
        axios.get(`${API}/portfolio`),
        axios.get(`${API}/brands`),
        axios.get(`${API}/site-images`),
        axios.get(`${API}/admin/leads/stats`, auth),
      ]);
      setLeads(l.data);
      setPortfolio(p.data);
      setBrands(b.data);
      setSiteImages(si.data || {});
      setStats(st.data);
    } catch {
      setToken('');
      localStorage.removeItem('dd-admin-token');
    }
  }, [token]);

  useEffect(() => {
    if (token) loadAll();
  }, [token, loadAll]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem('dd-admin-token', data.token);
      setToken(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Eroare la autentificare');
    }
  };

  const logout = () => {
    localStorage.removeItem('dd-admin-token');
    setToken('');
  };

  const uploadFiles = async (files) => {
    setUploadError('');
    const paths = [];
    for (const f of Array.from(files)) {
      const fd = new FormData();
      fd.append('file', f);
      try {
        const { data } = await axios.post(`${API}/admin/upload`, fd, auth);
        paths.push(data.path);
      } catch (err) {
        setUploadError(`Încărcarea a eșuat pentru „${f.name}": ${err.response?.data?.detail || 'eroare de rețea'}`);
      }
    }
    return paths;
  };

  const replaceSlot = async (key, file) => {
    if (!file) return;
    setUploading(key);
    try {
      const [path] = await uploadFiles([file]);
      await axios.put(`${API}/admin/site-images`, { key, value: path }, auth);
      setSiteImages((m) => ({ ...m, [key]: path }));
      window.dispatchEvent(new Event('dd-site-images-changed'));
    } finally {
      setUploading('');
    }
  };

  const addPhotosToItem = async (item, files) => {
    if (!files?.length) return;
    setUploading(item.id);
    try {
      const paths = await uploadFiles(files);
      updateLocal(setPortfolio, item.id, 'images', [...(item.images || []), ...paths]);
    } finally {
      setUploading('');
    }
  };

  const saveItem = async (item) => {
    await axios.put(`${API}/admin/portfolio/${item.id}`, {
      category: item.category, title: item.title, location: item.location || '',
      image: item.image, images: item.images,
    }, auth);
    loadAll();
  };

  const deleteItem = async (id) => {
    await axios.delete(`${API}/admin/portfolio/${id}`, auth);
    loadAll();
  };

  const addItem = async () => {
    if (!newItem.title || newImages.length === 0) return;
    await axios.post(`${API}/admin/portfolio`, { ...newItem, images: newImages }, auth);
    setNewItem({ category: 'pvc', title: '', location: '' });
    setNewImages([]);
    loadAll();
  };

  const saveBrand = async (b) => {
    await axios.put(`${API}/admin/brands/${b.id}`, { name: b.name }, auth);
    loadAll();
  };

  const deleteBrand = async (id) => {
    await axios.delete(`${API}/admin/brands/${id}`, auth);
    loadAll();
  };

  const addBrand = async () => {
    if (!newBrand.trim()) return;
    await axios.post(`${API}/admin/brands`, { name: newBrand.trim() }, auth);
    setNewBrand('');
    loadAll();
  };

  const updateLocal = (setter, id, key, value) =>
    setter((items) => items.map((it) => (it.id === id ? { ...it, [key]: value } : it)));

  const setLeadStatus = async (lead, status) => {
    await axios.put(`${API}/admin/leads/${lead.id}`, { status }, auth);
    setLeads((ls) => ls.map((x) => (x.id === lead.id ? { ...x, status } : x)));
  };

  const removeLead = async (lead) => {
    if (!window.confirm(`Ștergi lead-ul de la ${lead.name}?`)) return;
    await axios.delete(`${API}/admin/leads/${lead.id}`, auth);
    setLeads((ls) => ls.filter((x) => x.id !== lead.id));
    loadAll();
  };

  const exportExcel = async () => {
    const params = new URLSearchParams();
    if (exportFrom) params.set('from_date', exportFrom);
    if (exportTo) params.set('to_date', exportTo);
    const r = await axios.get(`${API}/admin/leads/export?${params.toString()}`, { ...auth, responseType: 'blob' });
    saveBlob(r.data, `leaduri_${exportFrom || 'toate'}_${exportTo || 'astazi'}.xlsx`);
  };

  const downloadLeadPdf = (lead) => {
    setSchemaLead(null);
    setPdfLead(lead);
  };

  useEffect(() => {
    if (!pdfLead) return undefined;
    const id = setTimeout(async () => {
      await downloadDiagramPDF({
        title: `Domidoor Solutions - ${stripDia(pdfLead.name)}`,
        rows: buildPdfRows(pdfLead.measurements || {}),
        filename: 'schema-masuratori.pdf',
      });
      setPdfLead(null);
    }, 200);
    return () => clearTimeout(id);
  }, [pdfLead]);

  const filteredLeads = leads.filter((l) => {
    if (!leadFilter) return true;
    const meta = l.meta || {};
    if (leadFilter.type === 'device') return (meta.device || 'necunoscut') === leadFilter.value;
    if (leadFilter.type === 'source') return (meta.source || 'direct') === leadFilter.value;
    return true;
  });

  const statCards = stats
    ? [
        { key: 'total', label: 'Total lead-uri', value: stats.total, filter: null },
        { key: 'month', label: 'Luna aceasta', value: stats.month, filter: null },
        { key: 'desktop', label: 'Desktop', value: (stats.devices || {}).desktop || 0, filter: { type: 'device', value: 'desktop' } },
        { key: 'mobil', label: 'Mobil', value: (stats.devices || {}).mobil || 0, filter: { type: 'device', value: 'mobil' } },
        { key: 'google', label: 'Google', value: (stats.sources || {}).google || 0, filter: { type: 'source', value: 'google' } },
        { key: 'facebook', label: 'Facebook', value: (stats.sources || {}).facebook || 0, filter: { type: 'source', value: 'facebook' } },
        { key: 'instagram', label: 'Instagram', value: (stats.sources || {}).instagram || 0, filter: { type: 'source', value: 'instagram' } },
        { key: 'direct', label: 'Direct', value: (stats.sources || {}).direct || 0, filter: { type: 'source', value: 'direct' } },
      ]
    : [];

  if (!token) {
    return (
      <main data-testid="admin-login-page" className="min-h-screen grid place-items-center px-6 pt-32 pb-16">
        <div className="w-full max-w-md bg-surface border border-line/10 rounded-[2rem] p-8 md:p-10 shadow-[0_30px_90px_rgba(24,26,32,0.10)]">
          <Overline className="mb-3">Domidoor Admin</Overline>
          <h1 className="font-display text-3xl font-medium mb-8">Autentificare</h1>
          <form onSubmit={submitLogin} className="space-y-6">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-smoke mb-1">Email</label>
              <input data-testid="admin-login-email" type="email" required className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-smoke mb-1">Parolă</label>
              <input data-testid="admin-login-password" type="password" required className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            {error && <p data-testid="admin-login-error" className="text-[#EF4444] text-xs">{error}</p>}
            <button data-testid="admin-login-submit" type="submit" className="w-full bg-accent text-white font-bold text-sm uppercase tracking-[0.2em] py-4 rounded-full hover:bg-accenth transition-colors">
              Intră în cont
            </button>
          </form>
        </div>
      </main>
    );
  }

  const tabs = [
    ['leaduri', `Lead-uri (${leads.length})`],
    ['portofoliu', `Portofoliu (${portfolio.length})`],
    ['imagini', 'Imagini site'],
    ['branduri', `Branduri (${brands.length})`],
  ];

  return (
    <main data-testid="admin-panel" className="max-w-[1440px] mx-auto px-6 md:px-12 pt-36 lg:pt-44 pb-24">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <div>
          <Overline className="mb-2">Domidoor Admin</Overline>
          <h1 className="font-display text-3xl md:text-4xl font-medium">Panou de administrare</h1>
        </div>
        <button data-testid="admin-logout" onClick={logout} className={`${btnCls} rounded-full border border-line/20 text-smoke hover:text-accent hover:border-accent`}>
          <LogOut size={14} className="inline mr-2" /> Ieșire
        </button>
      </div>

      <div className="inline-flex flex-wrap gap-1 mb-10 rounded-full bg-surface2/70 border border-line/10 p-1.5 max-w-full">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            data-testid={`admin-tab-${key}`}
            onClick={() => setTab(key)}
            className={`${btnCls} rounded-full px-5 py-2.5 whitespace-nowrap ${tab === key ? 'bg-ink text-paper shadow-[0_8px_24px_rgba(24,26,32,0.18)]' : 'text-smoke hover:text-ink'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {uploadError && <p data-testid="admin-upload-error" className="text-[#EF4444] text-sm mb-6">{uploadError}</p>}

      {tab === 'leaduri' && (
        <div>
          {stats && (
            <div data-testid="admin-leads-stats" className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-8">
              {statCards.map((c) => {
                const active = c.filter && leadFilter && leadFilter.type === c.filter.type && leadFilter.value === c.filter.value;
                return (
                  <button
                    key={c.key}
                    type="button"
                    data-testid={`admin-stat-${c.key}`}
                    onClick={() => setLeadFilter(c.filter ? (active ? null : c.filter) : null)}
                    className={`bg-surface border border-line/10 rounded-2xl p-4 md:p-5 text-left transition-[box-shadow,transform,border-color] duration-300 ${c.filter ? 'hover:shadow-[0_16px_40px_rgba(24,26,32,0.10)] hover:-translate-y-0.5 cursor-pointer' : 'cursor-default'} ${active ? 'ring-2 ring-accent/60 border-accent/40' : ''}`}
                  >
                    <span className={`font-display text-2xl md:text-3xl font-medium block ${active ? 'text-accent' : ''}`}>{c.value}</span>
                    <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-smoke">{c.label}</span>
                  </button>
                );
              })}
            </div>
          )}

          <div className="flex flex-wrap items-end gap-4 mb-6">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-smoke mb-1">De la</label>
              <input data-testid="admin-export-from" type="date" className={inputCls} value={exportFrom} onChange={(e) => setExportFrom(e.target.value)} />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-smoke mb-1">Până la</label>
              <input data-testid="admin-export-to" type="date" className={inputCls} value={exportTo} onChange={(e) => setExportTo(e.target.value)} />
            </div>
            <button data-testid="admin-export-excel" onClick={exportExcel} className={`${btnCls} rounded-full bg-ink text-paper hover:bg-accent hover:text-white`}>
              <FileSpreadsheet size={14} className="inline mr-2" /> Descarcă Excel
            </button>
            {leadFilter && (
              <button data-testid="admin-leads-clear-filter" onClick={() => setLeadFilter(null)} className={`${btnCls} rounded-full border border-line/20 text-smoke hover:text-accent hover:border-accent`}>
                <X size={14} className="inline mr-1" /> Resetare filtru ({filteredLeads.length})
              </button>
            )}
          </div>

          <div data-testid="admin-leads-table" className="overflow-x-auto bg-surface border border-line/10 rounded-[2rem] p-3 md:p-5 shadow-[0_20px_60px_rgba(24,26,32,0.06)]">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-smoke border-b border-line/10">
                  <th className="py-3 pr-4">Data</th><th className="py-3 pr-4">Nume</th><th className="py-3 pr-4">Telefon</th>
                  <th className="py-3 pr-4">Email</th><th className="py-3 pr-4">Oraș</th><th className="py-3 pr-4">Servicii</th>
                  <th className="py-3 pr-4">Tip</th><th className="py-3 pr-4">CUI</th><th className="py-3 pr-4">Status</th>
                  <th className="py-3 pr-4">Dimensiuni</th><th className="py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((l, i) => {
                  const hasMeas = l.measurements && Object.values(l.measurements).some(Boolean);
                  return (
                    <tr key={l.id || i} data-testid={`admin-lead-${i}`} className="border-b border-line/5 hover:bg-surface">
                      <td className="py-3 pr-4 whitespace-nowrap text-smoke">{new Date(l.created_at).toLocaleDateString('ro-RO')}</td>
                      <td className="py-3 pr-4 font-medium">{l.name}</td>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-1.5">
                          <a className="text-accent font-medium whitespace-nowrap" href={`tel:${l.phone}`}>{l.phone}</a>
                          <a data-testid={`admin-lead-call-${i}`} href={`tel:${l.phone}`} title="Sună acum" className="w-7 h-7 grid place-items-center rounded-full border border-line/20 text-smoke hover:text-accent hover:border-accent transition-colors shrink-0">
                            <Phone size={12} />
                          </a>
                          <a data-testid={`admin-lead-wapp-${i}`} href={waLink(l.phone)} target="_blank" rel="noreferrer" title="Deschide WhatsApp" className="w-7 h-7 grid place-items-center rounded-full bg-[#25D366]/10 text-[#1DA851] hover:bg-[#25D366] hover:text-white transition-colors shrink-0">
                            <MessageCircle size={12} />
                          </a>
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-smoke">{l.email}</td>
                      <td className="py-3 pr-4">{l.city}</td>
                      <td className="py-3 pr-4 text-smoke">{(l.services || []).map((s) => SVC_LABEL[s] || s).join(', ')}</td>
                      <td className="py-3 pr-4">{l.client_type}</td>
                      <td className="py-3 pr-4 text-smoke">{l.cui || '—'}</td>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[l.status || 'new']}`} />
                          <select
                            data-testid={`admin-lead-status-${i}`}
                            value={l.status || 'new'}
                            onChange={(e) => setLeadStatus(l, e.target.value)}
                            className={`bg-paper border border-line/15 rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] focus:outline-none focus:border-accent cursor-pointer ${STATUS_STYLE[l.status || 'new']}`}
                          >
                            {LEAD_STATUS_OPTIONS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                          </select>
                        </div>
                      </td>
                      <td className="py-3 pr-4 whitespace-nowrap">
                        {hasMeas ? (
                          <div className="flex gap-1.5">
                            <button data-testid={`admin-lead-schema-${i}`} onClick={() => setSchemaLead(l)} title="Vezi schema măsurătorilor" className={`${btnCls} border border-line/20 text-smoke hover:text-accent hover:border-accent`}>
                              <Ruler size={13} className="inline mr-1" /> Schemă
                            </button>
                            <button data-testid={`admin-lead-pdf-${i}`} onClick={() => downloadLeadPdf(l)} title="Descarcă PDF cu schema" className={`${btnCls} border border-line/20 text-smoke hover:text-accent hover:border-accent`}>
                              <FileDown size={13} />
                            </button>
                          </div>
                        ) : <span className="text-smoke">—</span>}
                      </td>
                      <td className="py-3">
                        <button data-testid={`admin-lead-delete-${i}`} onClick={() => removeLead(l)} title="Șterge lead-ul" className="w-8 h-8 grid place-items-center border border-line/20 text-smoke hover:text-[#EF4444] hover:border-[#EF4444] transition-colors">
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filteredLeads.length === 0 && <tr><td colSpan="11" className="py-8 text-smoke text-center">Niciun lead încă.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'portofoliu' && (
        <div className="space-y-4">
          {portfolio.map((item, i) => (
            <div key={item.id} data-testid={`admin-portfolio-row-${i}`} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end border border-line/10 rounded-2xl bg-surface p-5">
              <div className="md:col-span-3">
                <label className="block text-[10px] uppercase tracking-[0.2em] text-smoke mb-1.5">Poze ({(item.images || []).length})</label>
                <div data-testid={`admin-portfolio-photos-${i}`} className="flex flex-wrap gap-2 items-center">
                  {(item.images || []).map((img, j) => (
                    <div key={j} className="relative">
                      <img src={resolveImg(img)} alt="" className="w-16 h-12 object-cover border border-line/10" />
                      {j === 0 && <span className="absolute -top-2 left-0 bg-accent text-white text-[8px] font-bold uppercase px-1 py-px">copertă</span>}
                      {(item.images || []).length > 1 && (
                        <button
                          type="button"
                          data-testid={`admin-portfolio-photo-remove-${i}-${j}`}
                          onClick={() => updateLocal(setPortfolio, item.id, 'images', item.images.filter((_, k) => k !== j))}
                          className="absolute -top-2 -right-2 w-4 h-4 bg-[#EF4444] text-white rounded-full grid place-items-center"
                        >
                          <X size={10} />
                        </button>
                      )}
                    </div>
                  ))}
                  <label
                    data-testid={`admin-portfolio-photo-add-${i}`}
                    className="w-16 h-12 grid place-items-center border border-dashed border-line/25 cursor-pointer text-smoke hover:text-accent hover:border-accent transition-colors"
                  >
                    {uploading === item.id ? <span className="text-[9px]">...</span> : <ImagePlus size={16} />}
                    <input type="file" accept="image/*" multiple className="hidden" disabled={uploading === item.id} onChange={(e) => { addPhotosToItem(item, e.target.files); e.target.value = ''; }} />
                  </label>
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-[10px] uppercase tracking-[0.2em] text-smoke">Categorie</label>
                <select data-testid={`admin-portfolio-category-${i}`} className={`${inputCls} bg-paper`} value={item.category} onChange={(e) => updateLocal(setPortfolio, item.id, 'category', e.target.value)}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="md:col-span-3">
                <label className="block text-[10px] uppercase tracking-[0.2em] text-smoke">Titlu</label>
                <input data-testid={`admin-portfolio-title-${i}`} className={inputCls} value={item.title} onChange={(e) => updateLocal(setPortfolio, item.id, 'title', e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[10px] uppercase tracking-[0.2em] text-smoke">Locație</label>
                <input className={inputCls} value={item.location || ''} onChange={(e) => updateLocal(setPortfolio, item.id, 'location', e.target.value)} />
              </div>
              <div className="md:col-span-2 flex gap-2">
                <button data-testid={`admin-portfolio-save-${i}`} onClick={() => saveItem(item)} className={`${btnCls} bg-accent text-white hover:bg-accenth flex-1`}>Salvează</button>
                <button data-testid={`admin-portfolio-delete-${i}`} onClick={() => deleteItem(item.id)} className={`${btnCls} border border-line/20 text-smoke hover:text-[#EF4444] hover:border-[#EF4444]`}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end border border-dashed border-line/20 rounded-2xl p-5 mt-6">
            <div className="md:col-span-2">
              <label className="block text-[10px] uppercase tracking-[0.2em] text-smoke">Categorie</label>
              <select data-testid="admin-portfolio-new-category" className={`${inputCls} bg-paper`} value={newItem.category} onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="block text-[10px] uppercase tracking-[0.2em] text-smoke">Titlu</label>
              <input data-testid="admin-portfolio-new-title" className={inputCls} value={newItem.title} onChange={(e) => setNewItem({ ...newItem, title: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[10px] uppercase tracking-[0.2em] text-smoke">Locație</label>
              <input className={inputCls} value={newItem.location} onChange={(e) => setNewItem({ ...newItem, location: e.target.value })} />
            </div>
            <div className="md:col-span-3">
              <label className="block text-[10px] uppercase tracking-[0.2em] text-smoke mb-1.5">Poze ({newImages.length})</label>
              <div data-testid="admin-portfolio-new-photos" className="flex flex-wrap gap-2 items-center">
                {newImages.map((img, j) => (
                  <div key={j} className="relative">
                    <img src={resolveImg(img)} alt="" className="w-16 h-12 object-cover border border-line/10" />
                    <button
                      type="button"
                      onClick={() => setNewImages(newImages.filter((_, k) => k !== j))}
                      className="absolute -top-2 -right-2 w-4 h-4 bg-[#EF4444] text-white rounded-full grid place-items-center"
                    >
                      <X size={10} />
                    </button>
                  </div>
                ))}
                <label
                  data-testid="admin-portfolio-new-photo-add"
                  className="w-16 h-12 grid place-items-center border border-dashed border-line/25 cursor-pointer text-smoke hover:text-accent hover:border-accent transition-colors"
                >
                  {uploading === 'new' ? <span className="text-[9px]">...</span> : <ImagePlus size={16} />}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    disabled={uploading === 'new'}
                    onChange={async (e) => {
                      if (!e.target.files?.length) return;
                      setUploading('new');
                      try {
                        const paths = await uploadFiles(e.target.files);
                        setNewImages((imgs) => [...imgs, ...paths]);
                      } finally {
                        setUploading('');
                        e.target.value = '';
                      }
                    }}
                  />
                </label>
              </div>
            </div>
            <div className="md:col-span-2">
              <button data-testid="admin-portfolio-add" onClick={addItem} className={`${btnCls} bg-ink text-paper hover:bg-accent hover:text-white w-full`}><Plus size={14} className="inline mr-1" /> Adaugă</button>
            </div>
          </div>
        </div>
      )}

      {tab === 'imagini' && (
        <div data-testid="admin-images-tab" className="space-y-12">
          <p className="text-smoke text-sm max-w-2xl leading-relaxed">
            Aici înlocuiești imaginile site-ului, grupate pe locația exactă unde apar. Sloturile marcate „implicită” folosesc imaginea standard; după ce urci propria poză, devin „personalizată”. Pozele se comprimă automat (max 2000px) ca site-ul să rămână rapid.
          </p>
          {SITE_IMAGE_GROUPS.map((g) => (
            <div key={g.title}>
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-smoke mb-5">{g.title}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {g.slots.map((s) => (
                  <ImageSlotCard key={s.key} slot={s} custom={siteImages[s.key]} uploading={uploading === s.key} onReplace={replaceSlot} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'branduri' && (
        <div className="space-y-4 max-w-2xl">
          {brands.map((b, i) => (
            <div key={b.id} data-testid={`admin-brand-row-${i}`} className="flex gap-3 items-end border border-line/10 rounded-2xl bg-surface p-5">
              <div className="flex-1">
                <label className="block text-[10px] uppercase tracking-[0.2em] text-smoke">Nume brand</label>
                <input data-testid={`admin-brand-name-${i}`} className={inputCls} value={b.name} onChange={(e) => updateLocal(setBrands, b.id, 'name', e.target.value)} />
              </div>
              <button data-testid={`admin-brand-save-${i}`} onClick={() => saveBrand(b)} className={`${btnCls} bg-accent text-white hover:bg-accenth`}>Salvează</button>
              <button data-testid={`admin-brand-delete-${i}`} onClick={() => deleteBrand(b.id)} className={`${btnCls} border border-line/20 text-smoke hover:text-[#EF4444] hover:border-[#EF4444]`}><Trash2 size={14} /></button>
            </div>
          ))}
          <div className="flex gap-3 items-end border border-dashed border-line/20 rounded-2xl p-5">
            <div className="flex-1">
              <label className="block text-[10px] uppercase tracking-[0.2em] text-smoke">Brand nou</label>
              <input data-testid="admin-brand-new-name" className={inputCls} value={newBrand} onChange={(e) => setNewBrand(e.target.value)} />
            </div>
            <button data-testid="admin-brand-add" onClick={addBrand} className={`${btnCls} bg-ink text-paper hover:bg-accent hover:text-white`}><Plus size={14} className="inline mr-1" /> Adaugă</button>
          </div>
        </div>
      )}

      {schemaLead && (
        <div data-testid="lead-schema-modal" className="fixed inset-0 z-[100] bg-black/80 grid place-items-center p-4" onClick={() => setSchemaLead(null)}>
          <div className="bg-paper w-full max-w-4xl max-h-[90vh] overflow-auto p-6 md:p-8 relative" onClick={(e) => e.stopPropagation()}>
            <button data-testid="lead-schema-close" onClick={() => setSchemaLead(null)} className="absolute top-4 right-4 w-9 h-9 grid place-items-center border border-line/20 text-smoke hover:text-accent hover:border-accent transition-colors z-10">
              <X size={16} />
            </button>
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-smoke mb-1.5">Schema măsurători</p>
            <p data-testid="lead-schema-name" className="font-display text-xl font-medium mb-6">{schemaLead.name} — {schemaLead.city}</p>
            <GarageDiagram values={schemaLead.measurements} caption="" />
            <div className="flex flex-wrap gap-3 mt-6">
              <button data-testid="lead-schema-png" onClick={() => downloadDiagramPNG('schema-masuratori.png')} className={`${btnCls} bg-ink text-paper hover:bg-accent hover:text-white`}>
                <FileDown size={14} className="inline mr-2" /> Descarcă PNG
              </button>
              <button data-testid="lead-schema-pdf" onClick={() => downloadDiagramPDF({ title: `Domidoor Solutions - ${stripDia(schemaLead.name)}`, rows: buildPdfRows(schemaLead.measurements || {}), filename: 'schema-masuratori.pdf' })} className={`${btnCls} bg-accent text-white hover:bg-accenth`}>
                <FileDown size={14} className="inline mr-2" /> Descarcă PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {pdfLead && (
        <div style={{ position: 'fixed', left: '-99999px', top: 0, pointerEvents: 'none' }} aria-hidden="true">
          <GarageDiagram values={pdfLead.measurements} caption="" />
        </div>
      )}
    </main>
  );
}
